import { QuestionBankItemStatus } from '@features/question-bank/types/question-bank.types';
import { TestProgressStatus, TestStatus } from '@shared/enums';

/**
 * Get the CSS class for the status badge based on test status
 */
export const getStatusBadgeClass = (status: TestProgressStatus): string => {
  switch (status) {
    case TestProgressStatus.COMPLETED:
      return 'bg-emerald-100 text-emerald-600 dark:bg-emerald-400/25 dark:text-emerald-400';
    case TestProgressStatus.IN_PROGRESS:
      return 'bg-amber-100 text-amber-600 dark:bg-amber-400/25 dark:text-amber-400';
    case TestProgressStatus.NEW:
      return 'bg-indigo-500 text-white dark:bg-indigo-400/25 dark:text-indigo-400';
    default:
      return 'bg-slate-100 text-slate-500 dark:bg-slate-400/25 dark:text-slate-400';
  }
};

/**
 * Get the CSS class for the status icon background based on test status
 */
export const getStatusIconClass = (status: TestProgressStatus): string => {
  switch (status) {
    case TestProgressStatus.COMPLETED:
      return 'bg-emerald-500';
    case TestProgressStatus.IN_PROGRESS:
      return 'bg-amber-500';
    case TestProgressStatus.NEW:
      return 'bg-indigo-500';
    default:
      return 'bg-slate-500';
  }
};

/**
 * Get the CSS class for the status icon text color based on test status
 */
export const getStatusIconTextClass = (status: TestProgressStatus): string => {
  switch (status) {
    case TestProgressStatus.COMPLETED:
      return 'text-emerald-50';
    case TestProgressStatus.IN_PROGRESS:
      return 'text-amber-50';
    default:
      return 'text-slate-50';
  }
};

/**
 * Get the SVG path for the status icon based on test status
 */
export const getStatusIcon = (status: TestProgressStatus): string => {
  switch (status) {
    case TestProgressStatus.COMPLETED:
      return 'M8.994 20.006a1 1 0 0 1-.707-1.707l4.5-4.5a1 1 0 0 1 1.414 0l3.293 3.293 4.793-4.793a1 1 0 1 1 1.414 1.414l-5.5 5.5a1 1 0 0 1-1.414 0l-3.293-3.293L9.7 19.713a1 1 0 0 1-.707.293Z';
    case TestProgressStatus.IN_PROGRESS:
      return 'M16 0a16 16 0 1 1 0 32 16 16 0 0 1 0-32zm0 2.667a13.333 13.333 0 1 0 0 26.666 13.333 13.333 0 0 0 0-26.666zm0 24a10.667 10.667 0 1 1 0-21.334 10.667 10.667 0 0 1 0 21.334z';
    default:
      return 'M16 2.667A13.333 13.333 0 1 0 29.333 16 13.333 13.333 0 0 0 16 2.667zm-2.667 19.2a1.067 1.067 0 0 1-1.066-1.067V11.2a1.067 1.067 0 0 1 2.133 0v9.6a1.067 1.067 0 0 1-1.067 1.067z';
  }
};

/**
 * Get the display text for the status based on test status
 */
export const getStatusText = (status: TestProgressStatus): string => {
  switch (status) {
    case TestProgressStatus.COMPLETED:
      return 'Completed';
    case TestProgressStatus.IN_PROGRESS:
      return 'In Progress';
    case TestProgressStatus.NEW:
      return 'New';
    default:
      return 'Unknown';
  }
};

/**
 * Get the CSS class for the status badge based on test status
 */
export const getTestStatusBadgeClass = (
  status: TestStatus | QuestionBankItemStatus
): string => {
  switch (status) {
    case TestStatus.PUBLISHED:
      return 'bg-emerald-100 text-emerald-600 dark:bg-emerald-400/25 dark:text-emerald-400';
    case TestStatus.DRAFT:
      return 'bg-amber-100 text-amber-600 dark:bg-amber-400/25 dark:text-amber-400';
    default:
      return 'bg-slate-100 text-slate-500 dark:bg-slate-400/25 dark:text-slate-400';
  }
};

/**
 * Get the display text for the status based on test status
 */
export const getTestStatusText = (
  status: TestStatus | QuestionBankItemStatus
): string => {
  switch (status) {
    case TestStatus.PUBLISHED:
      return 'Published';
    case TestStatus.DRAFT:
      return 'Draft';
    default:
      return 'Unknown';
  }
};
